import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Clock, FileText, DollarSign, ChevronDown, ChevronUp, Pen, AlertCircle, CheckCircle2, Sun, Moon, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';
import BuilderContentRenderer from '../components/BuilderContentRenderer';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/uploads/qsecdef_logo.png`;

const billingLabels = {
  monthly: 'Monthly Subscription',
  annual: 'Annual Payment',
  one_time: 'One-time Payment',
  consultancy: 'Consultancy Services'
};

const billingDescriptions = {
  monthly: 'Billed monthly, cancel anytime',
  annual: 'Billed annually as a single payment',
  one_time: 'One-time payment',
  consultancy: '50% upfront, remainder billed every 5 days'
};

// Helper function to fix relative image URLs in HTML content
const fixImageUrls = (htmlContent) => {
  if (!htmlContent) return '';
  // Convert relative /api/public/uploads/ or /api/uploads/ paths to absolute URLs
  // Also handle any old preview URLs or legacy domains that might be stored
  return htmlContent
    // Relative paths
    .replace(/src=["']\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Preview domain URLs
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Legacy emergent.host domain URLs
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`);
};

const PublicProposal = () => {
  const { token, recipientToken } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [isDark, setIsDark] = useState(false); // Default to light mode for professional appearance
  const [paymentStatus, setPaymentStatus] = useState(null); // 'pending', 'completed', null
  const [signData, setSignData] = useState({
    signer_name: '',
    signer_email: '',
    signer_title: '',
    signature_data: '',
    legal_consent: false,
    signature_method: 'draw', // 'draw' or 'type'
    typed_signature: ''
  });
  const [declineData, setDeclineData] = useState({
    decliner_name: '',
    decliner_email: '',
    reason: '',
    feedback: ''
  });
  const [signing, setSigning] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [signed, setSigned] = useState(false);
  const [declined, setDeclined] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const sectionTimerRef = useRef(null);
  const currentSectionRef = useRef(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const proposalContentRef = useRef(null);

  // Helper function to strip HTML tags and get plain text
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitizeHTML(html || '');
    return tmp.textContent || tmp.innerText || '';
  };

  // Helper function to load image as base64
  const loadImageAsBase64 = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  // Parse HTML content into structured blocks for PDF rendering
  const parseHtmlToBlocks = (html) => {
    const blocks = [];
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitizeHTML(html || '');
    
    const processNode = (node, inheritedStyle = {}) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          blocks.push({ type: 'text', content: text, style: { ...inheritedStyle } });
        }
        return;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      
      const tagName = node.tagName.toLowerCase();
      const style = { ...inheritedStyle };
      
      // Check for text alignment
      const textAlign = node.style.textAlign || 
        (node.getAttribute('style') || '').match(/text-align:\s*(\w+)/)?.[1];
      if (textAlign) style.align = textAlign;
      
      switch (tagName) {
        case 'h1':
          blocks.push({ type: 'heading', level: 1, content: node.textContent.trim(), style });
          blocks.push({ type: 'spacing', size: 8 });
          break;
        case 'h2':
          blocks.push({ type: 'heading', level: 2, content: node.textContent.trim(), style });
          blocks.push({ type: 'spacing', size: 6 });
          break;
        case 'h3':
          blocks.push({ type: 'heading', level: 3, content: node.textContent.trim(), style });
          blocks.push({ type: 'spacing', size: 4 });
          break;
        case 'p':
          const pText = node.textContent.trim();
          if (pText) {
            blocks.push({ type: 'paragraph', content: pText, style });
            blocks.push({ type: 'spacing', size: 4 });
          } else {
            // Empty paragraph = extra spacing
            blocks.push({ type: 'spacing', size: 6 });
          }
          break;
        case 'br':
          blocks.push({ type: 'spacing', size: 3 });
          break;
        case 'ul':
        case 'ol':
          const isOrdered = tagName === 'ol';
          let itemIndex = 0;
          node.querySelectorAll(':scope > li').forEach(li => {
            itemIndex++;
            const bullet = isOrdered ? `${itemIndex}.` : '•';
            blocks.push({ type: 'listItem', content: li.textContent.trim(), bullet, style });
          });
          blocks.push({ type: 'spacing', size: 4 });
          break;
        case 'img':
          const src = node.getAttribute('src');
          if (src) {
            blocks.push({ type: 'image', src, style });
            blocks.push({ type: 'spacing', size: 4 });
          }
          break;
        case 'hr':
          blocks.push({ type: 'hr' });
          blocks.push({ type: 'spacing', size: 6 });
          break;
        case 'blockquote':
          blocks.push({ type: 'blockquote', content: node.textContent.trim(), style });
          blocks.push({ type: 'spacing', size: 4 });
          break;
        case 'table':
          const rows = [];
          node.querySelectorAll('tr').forEach(tr => {
            const cells = [];
            tr.querySelectorAll('th, td').forEach(cell => {
              cells.push({ content: cell.textContent.trim(), isHeader: cell.tagName === 'TH' });
            });
            if (cells.length > 0) rows.push(cells);
          });
          if (rows.length > 0) {
            blocks.push({ type: 'table', rows, style });
            blocks.push({ type: 'spacing', size: 6 });
          }
          break;
        case 'strong':
        case 'b':
          blocks.push({ type: 'text', content: node.textContent.trim(), style: { ...style, bold: true } });
          break;
        case 'em':
        case 'i':
          blocks.push({ type: 'text', content: node.textContent.trim(), style: { ...style, italic: true } });
          break;
        case 'div':
          // Check if it's a column layout
          if (node.classList.contains('column-layout') || node.style.display === 'grid') {
            // For columns, just process children inline for PDF (grids don't work well in PDF)
            node.childNodes.forEach(child => processNode(child, style));
            blocks.push({ type: 'spacing', size: 4 });
          } else {
            node.childNodes.forEach(child => processNode(child, style));
          }
          break;
        default:
          // Process children for other tags
          node.childNodes.forEach(child => processNode(child, style));
      }
    };
    
    tmp.childNodes.forEach(node => processNode(node));
    return blocks;
  };

  // PDF Download function using jsPDF with proper HTML handling
  const downloadPdf = async () => {
    setGeneratingPdf(true);
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      let currentPage = 1;

      // Colors
      const primaryGreen = [162, 255, 0]; // #A2FF00
      const darkGreen = [34, 197, 94]; // #22c55e for print-friendly green
      const darkText = [17, 17, 17];
      const grayText = [102, 102, 102];
      const lightGray = [245, 245, 245];

      // Helper function to add white background to each page
      const addPageBackground = () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      // Helper function to add footer to current page
      const addFooter = () => {
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text('QSECDEF - Membership Proposal', margin, pageHeight - 10);
        doc.text(`Page ${currentPage}`, pageWidth - margin - 15, pageHeight - 10);
      };

      // Helper function to check and add new page if needed
      const checkNewPage = (requiredSpace = 40) => {
        if (yPos > pageHeight - requiredSpace) {
          addFooter();
          doc.addPage();
          currentPage++;
          addPageBackground();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Start with white background
      addPageBackground();

      // Try to load logo
      let logoBase64 = null;
      try {
        logoBase64 = await loadImageAsBase64(LOGO_URL);
      } catch (e) {
        console.log('Could not load logo');
      }

      // === HEADER SECTION ===
      // Add logo if available
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', margin, yPos, 30, 30);
        yPos += 38;
      } else {
        yPos += 5;
      }

      // Title
      doc.setFontSize(24);
      doc.setTextColor(...darkText);
      doc.setFont('helvetica', 'bold');
      doc.text('Membership Proposal', margin, yPos);
      yPos += 12;

      // Client company name in green
      doc.setFontSize(18);
      doc.setTextColor(...darkGreen);
      doc.text(proposal.client?.company_name || 'Client', margin, yPos);
      yPos += 10;

      // Prepared for
      doc.setFontSize(11);
      doc.setTextColor(...grayText);
      doc.setFont('helvetica', 'normal');
      const contactName = `${proposal.client?.main_contact_first_name || ''} ${proposal.client?.main_contact_last_name || ''}`.trim();
      doc.text(`Prepared for: ${contactName || 'Client'}`, margin, yPos);
      yPos += 6;
      doc.text(`Date: ${formatDate(proposal.created_at)}`, margin, yPos);
      yPos += 8;

      // Header line
      doc.setDrawColor(...darkGreen);
      doc.setLineWidth(1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 18;

      // Helper function to render a block to PDF
      const renderBlock = async (block) => {
        switch (block.type) {
          case 'heading':
            checkNewPage(20);
            const headingSizes = { 1: 16, 2: 14, 3: 12 };
            doc.setFontSize(headingSizes[block.level] || 12);
            doc.setTextColor(...darkText);
            doc.setFont('helvetica', 'bold');
            const headingLines = doc.splitTextToSize(block.content, contentWidth);
            for (const line of headingLines) {
              checkNewPage(10);
              if (block.style?.align === 'center') {
                doc.text(line, pageWidth / 2, yPos, { align: 'center' });
              } else if (block.style?.align === 'right') {
                doc.text(line, pageWidth - margin, yPos, { align: 'right' });
              } else {
                doc.text(line, margin, yPos);
              }
              yPos += headingSizes[block.level] * 0.5;
            }
            break;
            
          case 'paragraph':
          case 'text':
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', block.style?.bold ? 'bold' : block.style?.italic ? 'italic' : 'normal');
            const pLines = doc.splitTextToSize(block.content, contentWidth);
            for (const line of pLines) {
              checkNewPage(8);
              if (block.style?.align === 'center') {
                doc.text(line, pageWidth / 2, yPos, { align: 'center' });
              } else if (block.style?.align === 'right') {
                doc.text(line, pageWidth - margin, yPos, { align: 'right' });
              } else {
                doc.text(line, margin, yPos);
              }
              yPos += 5;
            }
            break;
            
          case 'listItem':
            checkNewPage(8);
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'normal');
            doc.text(block.bullet, margin + 2, yPos);
            const itemLines = doc.splitTextToSize(block.content, contentWidth - 12);
            for (let i = 0; i < itemLines.length; i++) {
              if (i > 0) checkNewPage(6);
              doc.text(itemLines[i], margin + 10, yPos);
              yPos += 5;
            }
            break;
            
          case 'image':
            try {
              // Resolve relative URLs
              let imgSrc = block.src;
              if (imgSrc.startsWith('/')) {
                imgSrc = `${window.location.origin}${imgSrc}`;
              }
              const imgData = await loadImageAsBase64(imgSrc);
              if (imgData) {
                // Calculate image dimensions to fit page width
                const img = new Image();
                img.src = imgData;
                await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
                
                const maxWidth = contentWidth;
                const maxHeight = 80; // Max height in mm
                let imgWidth = img.width * 0.264583; // Convert px to mm (approx)
                let imgHeight = img.height * 0.264583;
                
                // Scale down if too large
                if (imgWidth > maxWidth) {
                  const scale = maxWidth / imgWidth;
                  imgWidth = maxWidth;
                  imgHeight = imgHeight * scale;
                }
                if (imgHeight > maxHeight) {
                  const scale = maxHeight / imgHeight;
                  imgHeight = maxHeight;
                  imgWidth = imgWidth * scale;
                }
                
                checkNewPage(imgHeight + 10);
                
                // Center the image
                const imgX = margin + (contentWidth - imgWidth) / 2;
                doc.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
                yPos += imgHeight + 4;
              }
            } catch (e) {
              console.log('Could not load image:', block.src);
            }
            break;
            
          case 'hr':
            checkNewPage(10);
            doc.setDrawColor(...lightGray);
            doc.setLineWidth(0.5);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 2;
            break;
            
          case 'blockquote':
            checkNewPage(15);
            doc.setFillColor(...lightGray);
            doc.setDrawColor(...darkGreen);
            const quoteLines = doc.splitTextToSize(block.content, contentWidth - 15);
            const quoteHeight = quoteLines.length * 5 + 6;
            doc.rect(margin, yPos - 3, contentWidth, quoteHeight, 'F');
            doc.setLineWidth(1);
            doc.line(margin, yPos - 3, margin, yPos - 3 + quoteHeight);
            doc.setFontSize(10);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'italic');
            for (const line of quoteLines) {
              doc.text(line, margin + 8, yPos);
              yPos += 5;
            }
            yPos += 3;
            break;
            
          case 'table':
            if (block.rows && block.rows.length > 0) {
              checkNewPage(30);
              const colCount = block.rows[0].length;
              const colWidth = contentWidth / colCount;
              const cellPadding = 2;
              
              for (const row of block.rows) {
                checkNewPage(12);
                let maxRowHeight = 8;
                
                // Draw cells
                row.forEach((cell, colIndex) => {
                  const cellX = margin + (colIndex * colWidth);
                  
                  // Cell background for headers
                  if (cell.isHeader) {
                    doc.setFillColor(...lightGray);
                    doc.rect(cellX, yPos - 5, colWidth, 8, 'F');
                  }
                  
                  // Cell border
                  doc.setDrawColor(200, 200, 200);
                  doc.setLineWidth(0.2);
                  doc.rect(cellX, yPos - 5, colWidth, 8);
                  
                  // Cell text
                  doc.setFontSize(9);
                  doc.setTextColor(...(cell.isHeader ? darkText : grayText));
                  doc.setFont('helvetica', cell.isHeader ? 'bold' : 'normal');
                  const cellText = doc.splitTextToSize(cell.content, colWidth - cellPadding * 2);
                  doc.text(cellText[0] || '', cellX + cellPadding, yPos);
                });
                
                yPos += maxRowHeight;
              }
              yPos += 4;
            }
            break;
            
          case 'spacing':
            yPos += block.size || 4;
            break;
        }
      };

      // === SECTIONS (Legacy format) ===
      if (proposal.sections && proposal.sections.length > 0) {
        for (const section of proposal.sections) {
          // Check if we need a new page (need at least 60mm for section header + some content)
          checkNewPage(60);

          // Section title with green left border
          doc.setFillColor(...darkGreen);
          doc.rect(margin, yPos - 5, 3, 10, 'F');
          
          doc.setFontSize(14);
          doc.setTextColor(...darkText);
          doc.setFont('helvetica', 'bold');
          doc.text(section.title, margin + 8, yPos);
          yPos += 12;

          // Parse and render section content as structured blocks
          const blocks = parseHtmlToBlocks(section.content);
          
          for (const block of blocks) {
            await renderBlock(block);
          }
          
          yPos += 8; // Extra spacing between sections
        }
      }

      // === BUILDER CONTENT (Craft.js format) ===
      if (proposal.builder_content) {
        let builderNodes;
        try {
          builderNodes = typeof proposal.builder_content === 'string' 
            ? JSON.parse(proposal.builder_content) 
            : proposal.builder_content;
        } catch (err) {
          console.error('Failed to parse builder content for PDF:', err);
          builderNodes = null;
        }

        if (builderNodes) {
          // Helper function to render Craft.js nodes recursively
          const renderCraftNode = async (nodeId) => {
            const node = builderNodes[nodeId];
            if (!node) return;

            const { type, props, nodes: childNodeIds } = node;
            const displayName = type?.resolvedName || type;

            // Render based on component type
            // Note: These names match the displayName in each component's .craft config
            switch (displayName) {
              case 'Canvas':
              case 'Section':
              case 'Column':
                // Just render children for container types
                if (childNodeIds && childNodeIds.length > 0) {
                  for (const childId of childNodeIds) {
                    await renderCraftNode(childId);
                  }
                }
                break;

              case 'Heading':
                checkNewPage(20);
                const headingLevel = props.level || 'h2';
                const headingSizes = { h1: 20, h2: 16, h3: 14 };
                doc.setFontSize(headingSizes[headingLevel] || 14);
                doc.setTextColor(...darkText);
                doc.setFont('helvetica', 'bold');
                
                const headingText = props.text || '';
                const headingLines = doc.splitTextToSize(headingText, contentWidth);
                for (const line of headingLines) {
                  checkNewPage(10);
                  if (props.textAlign === 'center') {
                    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
                  } else if (props.textAlign === 'right') {
                    doc.text(line, pageWidth - margin, yPos, { align: 'right' });
                  } else {
                    doc.text(line, margin, yPos);
                  }
                  yPos += headingSizes[headingLevel] * 0.5;
                }
                yPos += 4;
                break;

              case 'Text':
                doc.setFontSize(parseInt(props.fontSize) || 11);
                doc.setTextColor(...grayText);
                const fontWeight = props.fontWeight === 'bold' ? 'bold' : 'normal';
                doc.setFont('helvetica', fontWeight);
                
                const textContent = props.text || '';
                const textLines = doc.splitTextToSize(textContent, contentWidth);
                for (const line of textLines) {
                  checkNewPage(8);
                  if (props.textAlign === 'center') {
                    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
                  } else if (props.textAlign === 'right') {
                    doc.text(line, pageWidth - margin, yPos, { align: 'right' });
                  } else {
                    doc.text(line, margin, yPos);
                  }
                  yPos += 5;
                }
                yPos += 2;
                break;

              case 'Image':
                if (props.src) {
                  try {
                    let imgSrc = props.src;
                    if (imgSrc.startsWith('/')) {
                      imgSrc = `${window.location.origin}${imgSrc}`;
                    } else if (!imgSrc.startsWith('http')) {
                      imgSrc = `${BACKEND_URL}${imgSrc}`;
                    }
                    
                    const imgData = await loadImageAsBase64(imgSrc);
                    if (imgData) {
                      const img = new Image();
                      img.src = imgData;
                      await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
                      
                      // Calculate dimensions
                      const widthPercent = parseInt(props.width) || 100;
                      const maxWidth = contentWidth * (widthPercent / 100);
                      const maxHeight = 80;
                      
                      let imgWidth = img.width * 0.264583; // px to mm
                      let imgHeight = img.height * 0.264583;
                      
                      if (imgWidth > maxWidth) {
                        const scale = maxWidth / imgWidth;
                        imgWidth = maxWidth;
                        imgHeight = imgHeight * scale;
                      }
                      if (imgHeight > maxHeight) {
                        const scale = maxHeight / imgHeight;
                        imgHeight = maxHeight;
                        imgWidth = imgWidth * scale;
                      }
                      
                      checkNewPage(imgHeight + 10);
                      
                      // Position based on alignment
                      let imgX = margin;
                      if (props.alignment === 'center') {
                        imgX = margin + (contentWidth - imgWidth) / 2;
                      } else if (props.alignment === 'right') {
                        imgX = pageWidth - margin - imgWidth;
                      }
                      
                      doc.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
                      yPos += imgHeight + 6;
                    }
                  } catch (e) {
                    console.log('Could not load image for PDF:', props.src);
                  }
                }
                break;

              case 'Video':
                // Videos can't be embedded in PDF, add a placeholder
                checkNewPage(15);
                doc.setFillColor(...lightGray);
                doc.rect(margin, yPos - 3, contentWidth, 15, 'F');
                doc.setFontSize(10);
                doc.setTextColor(...grayText);
                doc.text('📹 Video content - view online', margin + 5, yPos + 5);
                yPos += 18;
                break;

              case 'Divider':
                checkNewPage(10);
                const spacing = props.spacing || 16;
                yPos += spacing * 0.264583; // px to mm
                if (props.style !== 'space') {
                  doc.setDrawColor(...lightGray);
                  doc.setLineWidth(0.5);
                  doc.line(margin, yPos, pageWidth - margin, yPos);
                }
                yPos += spacing * 0.264583;
                break;

              case 'Pricing Table':
                // Render pricing table from builder content
                checkNewPage(60);
                
                if (props.title) {
                  doc.setFontSize(14);
                  doc.setTextColor(...darkText);
                  doc.setFont('helvetica', 'bold');
                  doc.text(props.title, margin, yPos);
                  yPos += 10;
                }
                
                // Table header
                doc.setFontSize(9);
                doc.setTextColor(...grayText);
                doc.setFont('helvetica', 'bold');
                doc.text('Item', margin, yPos);
                if (props.showQuantity) {
                  doc.text('Qty', margin + 100, yPos);
                }
                doc.text('Price', margin + 130, yPos);
                yPos += 6;
                
                doc.setDrawColor(200, 200, 200);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 6;
                
                // Items
                const billingFreq = props.billingFrequency || 'annual';
                const currency = props.currency || '£';
                let subtotal = 0;
                
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(...darkText);
                
                for (const item of (props.items || [])) {
                  if (item.optional && !item.selected) continue;
                  
                  checkNewPage(12);
                  const itemPrice = billingFreq === 'monthly' 
                    ? (item.monthlyPrice || item.price || 0) 
                    : (item.annualPrice || item.price || 0);
                  const itemTotal = itemPrice * (item.quantity || 1);
                  subtotal += itemTotal;
                  
                  const itemName = (item.name || '').substring(0, 45);
                  doc.text(itemName, margin, yPos);
                  if (props.showQuantity) {
                    doc.text(String(item.quantity || 1), margin + 100, yPos);
                  }
                  doc.text(`${currency}${itemTotal.toLocaleString()}`, margin + 130, yPos);
                  yPos += 7;
                }
                
                // Totals
                yPos += 4;
                doc.setDrawColor(200, 200, 200);
                doc.line(margin + 100, yPos - 2, pageWidth - margin, yPos - 2);
                
                doc.setFontSize(10);
                doc.text(`Subtotal: ${currency}${subtotal.toLocaleString()}`, margin + 105, yPos + 3);
                yPos += 6;
                
                if (props.chargeVat) {
                  const vatRate = props.vatRate || 0.20;
                  const vatAmount = subtotal * vatRate;
                  doc.text(`VAT (${Math.round(vatRate * 100)}%): ${currency}${vatAmount.toLocaleString()}`, margin + 105, yPos + 3);
                  yPos += 6;
                  subtotal += vatAmount;
                }
                
                doc.setFontSize(12);
                doc.setTextColor(...darkGreen);
                doc.setFont('helvetica', 'bold');
                doc.text(`Total: ${currency}${subtotal.toLocaleString()}`, margin + 105, yPos + 3);
                yPos += 12;
                break;

              case 'Columns':
                // For columns, we'll render children sequentially (columns don't work well in PDF)
                if (childNodeIds && childNodeIds.length > 0) {
                  for (const childId of childNodeIds) {
                    await renderCraftNode(childId);
                  }
                }
                break;

              default:
                // Unknown type - try to render children
                if (childNodeIds && childNodeIds.length > 0) {
                  for (const childId of childNodeIds) {
                    await renderCraftNode(childId);
                  }
                }
            }
          };

          // Start rendering from ROOT node
          if (builderNodes.ROOT) {
            await renderCraftNode('ROOT');
          }
        }
      }

      // === PRICING SECTION ===
      if (proposal.pricing) {
        checkNewPage(80);

        // Pricing header
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 12;

        doc.setFontSize(16);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'bold');
        doc.text('Pricing Summary', margin, yPos);
        yPos += 14;

        // Handle pricing options (multiple plans)
        if (proposal.pricing.pricing_options?.length > 0) {
          for (const option of proposal.pricing.pricing_options) {
            checkNewPage(70);

            // Option name box
            doc.setFillColor(...lightGray);
            doc.rect(margin, yPos - 6, contentWidth, 12, 'F');
            doc.setFontSize(12);
            doc.setTextColor(...darkText);
            doc.setFont('helvetica', 'bold');
            doc.text(option.name, margin + 5, yPos);
            yPos += 14;

            // Table header
            doc.setFontSize(9);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'bold');
            doc.text('Item', margin, yPos);
            doc.text('Qty', margin + 95, yPos);
            doc.text('Price', margin + 115, yPos);
            doc.text('Total', margin + 145, yPos);
            yPos += 6;

            doc.setDrawColor(200, 200, 200);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 6;

            // Line items
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...darkText);
            for (const item of option.line_items || []) {
              checkNewPage(15);
              const itemName = (item.product_name || '').substring(0, 45);
              doc.text(itemName, margin, yPos);
              doc.text(String(item.quantity || 1), margin + 95, yPos);
              doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
              doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
              yPos += 7;
            }

            // Option total
            yPos += 5;
            doc.setDrawColor(200, 200, 200);
            doc.line(margin + 100, yPos - 3, pageWidth - margin, yPos - 3);
            doc.setFontSize(12);
            doc.setTextColor(...darkGreen);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total: £${(option.total_gbp || 0).toLocaleString()}`, margin + 125, yPos + 3);
            yPos += 18;
          }
        } 
        // Handle single pricing structure
        else if (proposal.pricing.line_items?.length > 0) {
          // Table header
          doc.setFontSize(9);
          doc.setTextColor(...grayText);
          doc.setFont('helvetica', 'bold');
          doc.text('Item', margin, yPos);
          doc.text('Qty', margin + 95, yPos);
          doc.text('Price', margin + 115, yPos);
          doc.text('Total', margin + 145, yPos);
          yPos += 6;

          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 6;

          // Line items
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...darkText);
          for (const item of proposal.pricing.line_items) {
            checkNewPage(15);
            const itemName = (item.product_name || '').substring(0, 45);
            doc.text(itemName, margin, yPos);
            doc.text(String(item.quantity || 1), margin + 95, yPos);
            doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
            yPos += 7;
          }

          // Totals
          yPos += 8;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin + 100, yPos - 5, pageWidth - margin, yPos - 5);
          doc.setFontSize(10);
          doc.setTextColor(...darkText);
          doc.text(`Subtotal: £${(proposal.pricing.subtotal_gbp || 0).toLocaleString()}`, margin + 115, yPos);
          yPos += 6;
          if (proposal.pricing.charge_vat && proposal.pricing.tax_amount_gbp > 0) {
            doc.text(`VAT (${Math.round((proposal.pricing.tax_rate || 0.20) * 100)}%): £${(proposal.pricing.tax_amount_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            yPos += 8;
          }
          doc.setFontSize(14);
          doc.setTextColor(...darkGreen);
          doc.setFont('helvetica', 'bold');
          doc.text(`Total: £${(proposal.pricing.total_gbp || 0).toLocaleString()}`, margin + 115, yPos);
        }
      }

      // === SIGNATURE SECTION (if signed) ===
      const sig = proposal.signature || proposal.signatures?.[0];
      if (signed && sig) {
        checkNewPage(80);

        yPos += 12;
        doc.setDrawColor(...darkGreen);
        doc.setLineWidth(2);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 15;

        // Green "CONTRACT SIGNED" badge
        doc.setFillColor(34, 197, 94);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.roundedRect(margin, yPos - 6, 60, 10, 2, 2, 'F');
        doc.text('CONTRACT SIGNED', margin + 5, yPos);
        yPos += 15;

        doc.setFontSize(10);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'normal');
        doc.text(`Signed by: ${sig.signer_name || ''}`, margin, yPos);
        yPos += 6;
        if (sig.signer_title) {
          doc.text(`Title: ${sig.signer_title}`, margin, yPos);
          yPos += 6;
        }
        if (sig.signer_email) {
          doc.text(`Email: ${sig.signer_email}`, margin, yPos);
          yPos += 6;
        }
        doc.text(`Date: ${formatDate(sig.signed_at)}`, margin, yPos);
        yPos += 12;
        
        // Add signature image if available
        if (sig.signature_data && sig.signature_data.startsWith('data:image')) {
          // Draw signature box
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(margin, yPos, 80, 35);
          
          doc.setFontSize(8);
          doc.setTextColor(...grayText);
          doc.text('Signature:', margin + 2, yPos + 5);
          
          try {
            doc.addImage(sig.signature_data, 'PNG', margin + 5, yPos + 8, 70, 22);
          } catch (e) {
            console.log('Could not add signature image');
            doc.setFontSize(10);
            doc.setTextColor(...darkText);
            doc.text('[Digital Signature on File]', margin + 10, yPos + 20);
          }
          yPos += 40;
        }

        // Legal statement
        yPos += 5;
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        const legalText = `This document was electronically signed on ${formatDate(sig.signed_at)}. The signer confirmed their agreement to the terms and conditions of this proposal.`;
        const legalLines = doc.splitTextToSize(legalText, contentWidth);
        for (const line of legalLines) {
          doc.text(line, margin, yPos);
          yPos += 4;
        }
      }

      // Add footer to last page
      addFooter();

      // Save the PDF
      doc.save(`proposal-${proposal.client?.company_name || 'document'}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  useEffect(() => {
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('proposal-theme');
    if (savedTheme) {
      setIsDark(savedTheme === 'dark');
    }
    fetchProposal();
    return () => {
      if (sectionTimerRef.current) {
        clearInterval(sectionTimerRef.current);
      }
    };
  }, [token]);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('proposal-theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    // Track section viewing time
    if (proposal && proposal.sections?.length > 0) {
      const currentSection = proposal.sections[activeSection];
      
      // Track section view immediately when entering a section
      if (currentSection?.section_id) {
        trackSectionView(currentSection.section_id, 0);
      }
      
      currentSectionRef.current = {
        section_id: currentSection?.section_id,
        start_time: Date.now(),
        last_tracked: Date.now()
      };

      // Set up periodic tracking every 10 seconds for longer views
      sectionTimerRef.current = setInterval(() => {
        if (currentSectionRef.current) {
          const duration = Math.round((Date.now() - currentSectionRef.current.last_tracked) / 1000);
          if (duration >= 10) {
            trackSectionView(currentSectionRef.current.section_id, duration);
            currentSectionRef.current.last_tracked = Date.now();
          }
        }
      }, 10000);

      // Track on page visibility change (when user switches tabs)
      const handleVisibilityChange = () => {
        if (document.hidden && currentSectionRef.current) {
          const duration = Math.round((Date.now() - currentSectionRef.current.last_tracked) / 1000);
          if (duration > 0) {
            trackSectionView(currentSectionRef.current.section_id, duration);
            currentSectionRef.current.last_tracked = Date.now();
          }
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Track on page unload
      const handleBeforeUnload = () => {
        if (currentSectionRef.current) {
          const duration = Math.round((Date.now() - currentSectionRef.current.last_tracked) / 1000);
          if (duration > 0) {
            // Use sendBeacon for reliable tracking on page unload
            const trackUrl = recipientToken
              ? `${BACKEND_URL}/api/p/${token}/r/${recipientToken}/track-section`
              : `${BACKEND_URL}/api/p/${token}/track-section`;
            navigator.sendBeacon(trackUrl, JSON.stringify({
              section_id: currentSectionRef.current.section_id,
              duration_seconds: duration
            }));
          }
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        // Report time spent when leaving section
        if (currentSectionRef.current) {
          const duration = Math.round((Date.now() - currentSectionRef.current.last_tracked) / 1000);
          if (duration > 0) {
            trackSectionView(currentSectionRef.current.section_id, duration);
          }
        }
        if (sectionTimerRef.current) {
          clearInterval(sectionTimerRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [activeSection, proposal, token, recipientToken]);

  // Initialize selected option when proposal loads
  useEffect(() => {
    if (proposal?.pricing?.pricing_options?.length > 0) {
      // Check if already selected
      if (proposal.pricing.selected_option_id) {
        setSelectedOptionId(proposal.pricing.selected_option_id);
      }
    }
  }, [proposal]);

  const fetchProposal = async () => {
    try {
      // Use recipient-specific endpoint if recipientToken is provided
      const apiUrl = recipientToken 
        ? `${BACKEND_URL}/api/p/${token}/r/${recipientToken}`
        : `${BACKEND_URL}/api/p/${token}`;
      
      const response = await fetch(apiUrl);
      if (response.ok) {
        const data = await response.json();
        setProposal(data);
        
        // Pre-fill signer/decliner email
        setSignData(prev => ({
          ...prev,
          signer_email: data.client?.main_contact_email || '',
          signer_name: `${data.client?.main_contact_first_name || ''} ${data.client?.main_contact_last_name || ''}`.trim()
        }));
        setDeclineData(prev => ({
          ...prev,
          decliner_email: data.client?.main_contact_email || '',
          decliner_name: `${data.client?.main_contact_first_name || ''} ${data.client?.main_contact_last_name || ''}`.trim()
        }));
        
        if (data.status === 'signed') {
          setSigned(true);
        }
        if (data.status === 'declined') {
          setDeclined(true);
        }
        
        // Set payment status from proposal data
        if (data.payment_status) {
          setPaymentStatus(data.payment_status);
        } else if (data.status === 'signed') {
          // If signed but no payment_status, default to pending
          setPaymentStatus('pending');
        }
        
        // Track view (only for non-recipient links, recipient tracking is done by backend)
        if (!recipientToken) {
          trackView();
        }
      } else {
        // Handle different HTTP error codes
        let errorMessage = 'Unable to load proposal';
        try {
          const errData = await response.json();
          errorMessage = errData.detail || errorMessage;
        } catch (parseErr) {
          // JSON parsing failed, use status-based message
          if (response.status === 404) {
            errorMessage = 'Proposal not found';
          } else if (response.status === 410) {
            errorMessage = 'Proposal has expired';
          } else if (response.status === 403) {
            errorMessage = 'Access denied';
          }
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Proposal fetch error:', err);
      setError('Unable to connect to server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/p/${token}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'opened' })
      });
    } catch (err) {
      console.error('Failed to track view');
    }
  };

  const trackSectionView = async (sectionId, duration) => {
    if (!sectionId) {
      console.warn('trackSectionView called without sectionId');
      return;
    }
    
    try {
      // Use recipient-specific tracking endpoint if available
      const trackUrl = recipientToken
        ? `${BACKEND_URL}/api/p/${token}/r/${recipientToken}/track-section`
        : `${BACKEND_URL}/api/p/${token}/track-section`;
      
      const response = await fetch(trackUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          section_id: sectionId,
          duration_seconds: duration
        })
      });
      
      if (!response.ok) {
        console.warn('Section tracking failed:', response.status);
      }
    } catch (err) {
      console.error('Failed to track section view:', err);
    }
  };

  const selectPricingOption = async (optionId) => {
    setSelectedOptionId(optionId);
    
    // Persist selection to backend
    try {
      await fetch(`${BACKEND_URL}/api/p/${token}/select-option`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_id: optionId })
      });
    } catch (err) {
      console.error('Failed to save option selection');
    }
  };

  const signProposal = async () => {
    // Validate pricing option selection if options exist
    const hasPricingOptions = proposal?.pricing?.pricing_options?.length > 0;
    if (hasPricingOptions && !selectedOptionId) {
      alert('Please select a payment option before signing');
      return;
    }
    
    if (!signData.signer_name || !signData.signer_email || !signData.legal_consent) {
      alert('Please fill in all required fields');
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas ? canvas.toDataURL('image/png') : '';

      const response = await fetch(`${BACKEND_URL}/api/p/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...signData,
          signature_data: signatureData
        })
      });

      if (response.ok) {
        setSigned(true);
        setShowSignModal(false);
        // Redirect to payment setup page after successful signing
        navigate(`/p/${token}/payment`);
      } else {
        const errData = await response.json();
        alert(errData.detail || 'Failed to sign proposal');
      }
    } catch (err) {
      alert('Failed to sign proposal');
    } finally {
      setSigning(false);
    }
  };

  const declineProposal = async () => {
    if (!declineData.decliner_name || !declineData.decliner_email || !declineData.reason) {
      alert('Please fill in all required fields');
      return;
    }

    setDeclining(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/p/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(declineData)
      });

      if (response.ok) {
        setDeclined(true);
        setShowDeclineModal(false);
      } else {
        const errData = await response.json();
        alert(errData.detail || 'Failed to submit feedback');
      }
    } catch (err) {
      alert('Failed to submit feedback');
    } finally {
      setDeclining(false);
    }
  };

  // Canvas drawing functions
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    // Scale coordinates to account for CSS sizing vs canvas internal size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = isDark ? '#A2FF00' : '#16a34a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setHasSignature(true);
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = isDark ? '#111' : '#f9fafb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  useEffect(() => {
    // Initialize canvas background and reset signature state
    if (showSignModal) {
      setHasSignature(false);
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = isDark ? '#111' : '#f9fafb';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }, 100);
    }
  }, [showSignModal, isDark]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} text-xl`}>Loading proposal...</div>
      </div>
    );
  }

  if (error) {
    const isNotFound = error.toLowerCase().includes('not found');
    const isExpired = error.toLowerCase().includes('expired');
    
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'} flex items-center justify-center p-4`}>
        <div className="text-center max-w-md">
          <AlertCircle className={`w-16 h-16 ${isExpired ? 'text-yellow-400' : 'text-red-400'} mx-auto mb-4`} />
          <h1 className={`text-2xl ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            {isExpired ? 'Proposal Expired' : isNotFound ? 'Proposal Not Found' : 'Unable to Load Proposal'}
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-6`}>{error}</p>
          {isNotFound && (
            <div className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-100 border-gray-200'} border rounded-lg text-left`}>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>This can happen if:</p>
              <ul className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} list-disc list-inside space-y-1`}>
                <li>The proposal link has expired or been revoked</li>
                <li>The link was copied incorrectly</li>
                <li>The proposal was deleted by the sender</li>
              </ul>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-3`}>
                Please contact the sender for a new proposal link.
              </p>
            </div>
          )}
          {isExpired && (
            <div className={`p-4 ${isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'} border rounded-lg text-left`}>
              <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                This proposal has passed its validity date. Please contact the sender to request an updated proposal with a new expiration date.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!proposal) return null;

  const currentSection = proposal.sections?.[activeSection];
  const hasPricingOptions = proposal.pricing?.pricing_options?.length > 0;
  const selectedOption = hasPricingOptions 
    ? proposal.pricing.pricing_options.find(o => o.option_id === selectedOptionId)
    : null;

  // Render pricing options side by side
  const renderPricingOptions = () => {
    if (!hasPricingOptions) {
      // Legacy pricing display
      return proposal.pricing?.line_items?.length > 0 && (
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
          <table className="w-full">
            <thead>
              <tr className={isDark ? 'bg-[#111]' : 'bg-gray-50'}>
                <th className={`text-left text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase p-4`}>Item</th>
                <th className={`text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase p-4`}>Qty</th>
                <th className={`text-right text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase p-4`}>Price</th>
              </tr>
            </thead>
            <tbody>
              {proposal.pricing.line_items.map((item, index) => (
                <tr key={index} className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-4">
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{item.product_name}</div>
                    <div className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-sm`}>{item.description}</div>
                  </td>
                  <td className={`text-center ${isDark ? 'text-gray-400' : 'text-gray-500'} p-4`}>{item.quantity}</td>
                  <td className={`text-right ${isDark ? 'text-white' : 'text-gray-900'} p-4`}>£{item.total_gbp.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <td colSpan="2" className={`text-right ${isDark ? 'text-gray-400' : 'text-gray-500'} p-4`}>Subtotal</td>
                <td className={`text-right ${isDark ? 'text-white' : 'text-gray-900'} p-4`}>£{proposal.pricing.subtotal_gbp.toLocaleString()}</td>
              </tr>
              {proposal.pricing.charge_vat && proposal.pricing.tax_amount_gbp > 0 && (
                <tr>
                  <td colSpan="2" className={`text-right ${isDark ? 'text-gray-400' : 'text-gray-500'} p-4`}>VAT ({Math.round((proposal.pricing.tax_rate || 0.20) * 100)}%)</td>
                  <td className={`text-right ${isDark ? 'text-white' : 'text-gray-900'} p-4`}>£{proposal.pricing.tax_amount_gbp.toLocaleString()}</td>
                </tr>
              )}
              <tr className={isDark ? 'bg-[#111]' : 'bg-gray-50'}>
                <td colSpan="2" className={`text-right ${isDark ? 'text-white' : 'text-gray-900'} font-bold p-4 text-lg`}>Total</td>
                <td className={`text-right ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold p-4 text-lg`}>
                  £{proposal.pricing.total_gbp.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    // New side-by-side pricing options
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Choose Your Payment Plan
          </h3>
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Select the option that works best for you</p>
        </div>

        <div className={`grid ${proposal.pricing.pricing_options.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
          {proposal.pricing.pricing_options.map((option) => {
            const isSelected = selectedOptionId === option.option_id;
            
            return (
              <div
                key={option.option_id}
                onClick={() => !signed && selectPricingOption(option.option_id)}
                className={`relative border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                  isSelected 
                    ? `${isDark ? 'border-[#A2FF00] shadow-lg shadow-[#A2FF00]/20' : 'border-green-500 shadow-lg shadow-green-500/20'}` 
                    : `${isDark ? 'border-white/20 hover:border-white/40' : 'border-gray-200 hover:border-gray-400'}`
                } ${signed ? 'cursor-default' : ''}`}
              >
                {/* Selected Badge */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <CheckCircle2 className={`w-6 h-6 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'}`} />
                  </div>
                )}
                
                {/* Header */}
                <div className={`p-6 ${isSelected ? (isDark ? 'bg-[#A2FF00]/10' : 'bg-green-50') : (isDark ? 'bg-[#111]' : 'bg-gray-50')}`}>
                  <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                    {option.name}
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {billingDescriptions[option.billing_frequency]}
                  </p>
                </div>
                
                {/* Price */}
                <div className={`p-6 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
                  <div className="text-center mb-6">
                    <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      £{option.total_gbp.toLocaleString()}
                    </span>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                      {option.billing_frequency === 'monthly' ? '/month' : 
                       option.billing_frequency === 'annual' ? '/year' : ''}
                    </span>
                    {option.charge_vat && option.tax_amount_gbp > 0 && (
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>inc. VAT</p>
                    )}
                  </div>
                  
                  {/* Line Items */}
                  <div className={`space-y-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-4`}>
                    {option.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start text-sm">
                        <div>
                          <span className={isDark ? 'text-white' : 'text-gray-900'}>{item.product_name}</span>
                          {item.quantity > 1 && (
                            <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'} ml-1`}>x{item.quantity}</span>
                          )}
                        </div>
                        <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                          £{item.total_gbp.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Breakdown */}
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} space-y-2 text-sm`}>
                    {option.charge_vat && option.tax_amount_gbp > 0 ? (
                      <>
                        <div className={`flex justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span>Subtotal</span>
                          <span>£{option.subtotal_gbp.toLocaleString()}</span>
                        </div>
                        <div className={`flex justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span>VAT ({Math.round((option.tax_rate || 0.20) * 100)}%)</span>
                          <span>£{option.tax_amount_gbp.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <div className={`flex justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>Total</span>
                        <span>£{(option.subtotal_gbp || option.total_gbp).toLocaleString()}</span>
                      </div>
                    )}
                    
                    {/* Show annual total for monthly subscriptions */}
                    {option.billing_frequency === 'monthly' && (
                      <div className={`flex justify-between font-bold pt-2 border-t ${isDark ? 'border-white/10 text-[#A2FF00]' : 'border-gray-200 text-green-600'}`}>
                        <span>Annual Total</span>
                        <span>£{((option.total_gbp || option.subtotal_gbp || 0) * 12).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Select Button */}
                {!signed && (
                  <div className={`p-4 ${isDark ? 'bg-[#111] border-t border-white/10' : 'bg-gray-50 border-t border-gray-200'}`}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        selectPricingOption(option.option_id);
                      }}
                      className={`w-full py-3 rounded font-bold transition-colors ${
                        isSelected
                          ? `${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'}`
                          : `${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
                      }`}
                    >
                      {isSelected ? '✓ Selected' : 'Select This Option'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Selection Prompt */}
        {!signed && !selectedOptionId && (
          <div className={`text-center p-4 ${isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'} border rounded`}>
            <p className={`text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
              Please select a payment option above before signing the proposal
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border-b sticky top-0 z-10`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} rounded-sm flex items-center justify-center`}>
              <span className={`${isDark ? 'text-black' : 'text-white'} font-bold text-lg`}>Q</span>
            </div>
            <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              QSECDEF
            </span>
          </div>
          <div className="flex items-center gap-4">
            {/* PDF Download Button */}
            <button
              onClick={downloadPdf}
              disabled={generatingPdf}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-white/10 text-white hover:bg-white/20' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${generatingPdf ? 'opacity-50 cursor-wait' : ''}`}
              title="Download as PDF"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline text-sm">{generatingPdf ? 'Generating...' : 'Download PDF'}</span>
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-white/10 text-white hover:bg-white/20' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            
            {signed ? (
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-sm">
                  <Check className="w-5 h-5" />
                  Signed
                </span>
                {/* Payment Status */}
                {paymentStatus === 'completed' ? (
                  <span className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-sm">
                    <CheckCircle2 className="w-5 h-5" />
                    Paid
                  </span>
                ) : paymentStatus === 'pending' || !paymentStatus ? (
                  <button
                    onClick={() => navigate(`/p/${token}/payment`)}
                    className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors`}
                  >
                    <DollarSign className="w-5 h-5" />
                    Complete Payment
                  </button>
                ) : null}
              </div>
            ) : declined ? (
              <span className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-sm">
                <AlertCircle className="w-5 h-5" />
                Declined
              </span>
            ) : (
              <span className={`flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>
                <Clock className="w-4 h-4" />
                Valid until {formatDate(proposal.expires_at)}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 sticky top-24`}>
              {/* Section Navigation - only for classic proposals */}
              {!proposal.builder_content && proposal.sections?.length > 0 && (
                <>
                  <h3 className={`text-xs font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase mb-4`}>Navigation</h3>
                  <nav className="space-y-1">
                    {proposal.sections?.map((section, index) => (
                      <button
                        key={section.section_id}
                        onClick={() => setActiveSection(index)}
                        className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                          activeSection === index
                            ? `${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-700'}`
                            : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </>
              )}
              
              {/* For builder proposals, show a simple info card */}
              {proposal.builder_content && (
                <>
                  <h3 className={`text-xs font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase mb-4`}>Proposal</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Scroll down to view the full proposal content and pricing.
                  </p>
                </>
              )}
              
              {/* Selected Option Summary */}
              {hasPricingOptions && selectedOption && (
                <div className={`mt-6 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <h4 className={`text-xs font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'} uppercase mb-2`}>Selected Plan</h4>
                  <div className={`${isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00]/20' : 'bg-green-50 border-green-200'} border rounded p-3`}>
                    <p className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-medium text-sm`}>{selectedOption.name}</p>
                    <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold`}>£{selectedOption.total_gbp.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Cover Info */}
            <div className={`${isDark ? 'bg-gradient-to-r from-[#A2FF00]/10 to-transparent border-[#A2FF00]' : 'bg-gradient-to-r from-green-100 to-transparent border-green-500'} border-l-4 p-6 mb-8`}>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Membership Proposal
              </h1>
              <p className={`text-xl ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>{proposal.client?.company_name}</p>
              <div className={`flex items-center gap-6 mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <span>Prepared for: {proposal.client?.main_contact_first_name} {proposal.client?.main_contact_last_name}</span>
                <span>Date: {formatDate(proposal.created_at)}</span>
              </div>
            </div>

            {/* Section Content - Builder or Classic */}
            {proposal.builder_content ? (
              /* Visual Builder Content */
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 mb-8`}>
                <BuilderContentRenderer content={proposal.builder_content} isDark={isDark} />
                
                {/* Still show pricing options if available */}
                {hasPricingOptions && (
                  <div className="mt-8">
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      Pricing Options
                    </h2>
                    {renderPricingOptions()}
                  </div>
                )}
              </div>
            ) : currentSection && (
              /* Classic Sections Content */
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 mb-8`}>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {currentSection.title}
                </h2>
                
                {currentSection.section_type === 'pricing' ? (
                  <div className="space-y-6">
                    <div 
                      className={`prose max-w-none proposal-content ${isDark ? 'prose-invert prose-p:text-gray-300 prose-li:text-gray-300' : 'prose-p:text-gray-700 prose-li:text-gray-700 prose-headings:text-gray-900'}`}
                      dangerouslySetInnerHTML={{ 
                        __html: sanitizeHTML(fixImageUrls((currentSection.content || '')
                          .replace(/<p><em>Note: The pricing table will be displayed below this text in the proposal viewer\.<\/em><\/p>/gi, '')
                          .replace(/Note: The pricing table will be displayed below this text in the proposal viewer\./gi, '')))
                      }}
                    />
                    
                    {/* Pricing Options or Legacy Pricing */}
                    {renderPricingOptions()}
                  </div>
                ) : currentSection.editor_mode === 'visual' && currentSection.visual_content ? (
                  /* Visual Section Content */
                  <BuilderContentRenderer content={currentSection.visual_content} isDark={isDark} />
                ) : (
                  /* Rich Text Section Content */
                  <div 
                    className={`prose max-w-none prose-headings:font-unbounded prose-h2:text-xl prose-h3:text-lg prose-p:my-4 prose-p:leading-relaxed proposal-content ${isDark ? 'prose-invert prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-[#A2FF00] prose-headings:text-white' : 'prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-green-600 prose-headings:text-gray-900'}`}
                    dangerouslySetInnerHTML={{ __html: sanitizeHTML(fixImageUrls(currentSection.content)) }}
                  />
                )}
              </div>
            )}

            {/* Navigation Buttons - only for classic proposals with sections */}
            {!proposal.builder_content && (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                  disabled={activeSection === 0}
                  className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-700 hover:border-green-500'} border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <ChevronUp className="w-4 h-4" />
                  Previous
                </button>
                
                {activeSection < (proposal.sections?.length || 0) - 1 ? (
                  <button
                    onClick={() => setActiveSection(activeSection + 1)}
                    className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors`}
                  >
                    Next
                    <ChevronDown className="w-4 h-4" />
                  </button>
                ) : signed && (paymentStatus === 'pending' || !paymentStatus) ? (
                /* Payment Pending - Show payment button */
                <div className="flex items-center gap-3">
                  <div className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'} rounded-sm`}>
                    <Clock className="w-5 h-5" />
                    Payment Pending
                  </div>
                  <button
                    onClick={() => navigate(`/p/${token}/payment`)}
                    className={`flex items-center gap-2 px-6 py-3 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors text-lg`}
                  >
                    <DollarSign className="w-5 h-5" />
                    Complete Payment
                  </button>
                </div>
              ) : signed && paymentStatus === 'completed' ? (
                /* Payment Complete */
                <div className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'} rounded-sm`}>
                  <CheckCircle2 className="w-5 h-5" />
                  Contract Signed & Payment Complete
                </div>
              ) : !signed && !declined && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowDeclineModal(true)}
                    className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-100 text-red-600 hover:bg-red-200'} text-sm rounded-sm transition-colors`}
                  >
                    Decline Proposal
                  </button>
                  <button
                    onClick={() => setShowSignModal(true)}
                    disabled={hasPricingOptions && !selectedOptionId}
                    className={`flex items-center gap-2 px-6 py-3 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors text-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <Pen className="w-5 h-5" />
                    Sign & Accept Proposal
                  </button>
                </div>
              )}
            </div>
            )}
            
            {/* Action buttons for Builder proposals */}
            {proposal.builder_content && (
              <div className="flex items-center justify-end mt-6">
                {signed && (paymentStatus === 'pending' || !paymentStatus) ? (
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'} rounded-sm`}>
                      <Clock className="w-5 h-5" />
                      Payment Pending
                    </div>
                    <button
                      onClick={() => navigate(`/p/${token}/payment`)}
                      className={`flex items-center gap-2 px-6 py-3 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors text-lg`}
                    >
                      <DollarSign className="w-5 h-5" />
                      Complete Payment
                    </button>
                  </div>
                ) : signed && paymentStatus === 'completed' ? (
                  <div className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'} rounded-sm`}>
                    <CheckCircle2 className="w-5 h-5" />
                    Payment Complete - Thank You!
                  </div>
                ) : !signed && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowDeclineModal(true)}
                      className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-red-500' : 'bg-white border-gray-300 text-gray-700 hover:border-red-500'} border rounded-sm transition-colors`}
                    >
                      <AlertCircle className="w-4 h-4" />
                      Decline
                    </button>
                    <button
                      onClick={() => setShowSignModal(true)}
                      disabled={hasPricingOptions && !selectedOptionId}
                      className={`flex items-center gap-2 px-6 py-3 ${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-bold rounded-sm hover:opacity-90 transition-colors text-lg disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Pen className="w-5 h-5" />
                      Sign & Accept Proposal
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/90' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Sign Proposal
              </h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mt-1`}>
                By signing, you agree to the terms outlined in this proposal.
              </p>
              
              {/* Show selected option */}
              {selectedOption && (
                <div className={`mt-4 p-3 ${isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00]/20' : 'bg-green-50 border-green-200'} border rounded`}>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase mb-1`}>Selected Plan</p>
                  <div className="flex justify-between items-center">
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{selectedOption.name}</span>
                    <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-bold`}>£{selectedOption.total_gbp.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Full Name *</label>
                  <input
                    type="text"
                    value={signData.signer_name}
                    onChange={(e) => setSignData({ ...signData, signer_name: e.target.value })}
                    required
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Title</label>
                  <input
                    type="text"
                    value={signData.signer_title}
                    onChange={(e) => setSignData({ ...signData, signer_title: e.target.value })}
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Email *</label>
                <input
                  type="email"
                  value={signData.signer_email}
                  onChange={(e) => setSignData({ ...signData, signer_email: e.target.value })}
                  required
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                />
              </div>
              
              {/* Signature Method Toggle */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Signature Method *</label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSignData({ ...signData, signature_method: 'draw' });
                      setHasSignature(false);
                    }}
                    className={`flex-1 px-3 py-2 text-sm rounded-sm transition-colors ${
                      signData.signature_method !== 'type'
                        ? `${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-medium`
                        : `${isDark ? 'bg-[#111] border-white/20 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} border`
                    }`}
                  >
                    ✍️ Draw Signature
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSignData({ ...signData, signature_method: 'type', typed_signature: signData.signer_name || '' });
                      setHasSignature(signData.signer_name?.length > 0);
                    }}
                    className={`flex-1 px-3 py-2 text-sm rounded-sm transition-colors ${
                      signData.signature_method === 'type'
                        ? `${isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'} font-medium`
                        : `${isDark ? 'bg-[#111] border-white/20 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} border`
                    }`}
                  >
                    ⌨️ Type Full Name
                  </button>
                </div>
                
                {/* Draw Signature Canvas */}
                {signData.signature_method !== 'type' && (
                  <div>
                    <div className={`border ${isDark ? 'border-white/20' : 'border-gray-300'} rounded-sm overflow-hidden`}>
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={clearCanvas}
                      className={`mt-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Clear Signature
                    </button>
                  </div>
                )}
                
                {/* Type Signature Input */}
                {signData.signature_method === 'type' && (
                  <div>
                    <div className={`border-2 ${isDark ? 'border-white/20 bg-[#111]' : 'border-gray-300 bg-gray-50'} rounded-sm p-4`}>
                      <input
                        type="text"
                        value={signData.typed_signature || ''}
                        onChange={(e) => {
                          setSignData({ ...signData, typed_signature: e.target.value });
                          setHasSignature(e.target.value.trim().length > 0);
                        }}
                        placeholder="Type your full legal name"
                        className={`w-full text-center text-2xl italic bg-transparent border-none outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                        style={{ fontFamily: "'Brush Script MT', cursive, serif" }}
                      />
                      <p className={`text-center text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        This typed name will serve as your legally binding electronic signature
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Legal Consent Checkbox */}
              <label className={`flex items-start gap-3 p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded cursor-pointer`}>
                <input
                  type="checkbox"
                  checked={signData.legal_consent}
                  onChange={(e) => setSignData({ ...signData, legal_consent: e.target.checked })}
                  className="mt-1"
                />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  I confirm that I have read and understood the proposal, and I accept the terms and conditions outlined herein. 
                  I am authorized to enter into this agreement on behalf of <strong className={isDark ? 'text-white' : 'text-gray-900'}>{proposal.client?.company_name}</strong>.
                </span>
              </label>
              
              {/* Legal Compliance Notice */}
              <div className={`p-3 ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'} border rounded text-xs`}>
                <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} font-medium mb-1`}>🔒 Electronic Signature Compliance</p>
                <p className={`${isDark ? 'text-blue-200/70' : 'text-blue-600'}`}>
                  This electronic signature is legally binding under the UK Electronic Communications Act 2000, 
                  EU eIDAS Regulation, and US ESIGN Act. Your signature, timestamp, and IP address will be 
                  recorded as part of the audit trail.
                </p>
              </div>
            </div>
            
            <div className={`p-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex flex-col gap-3`}>
              {/* Signature required hint */}
              {!hasSignature && (
                <p className={`text-sm text-center ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {signData.signature_method === 'type' 
                    ? 'Please type your full legal name above to enable signing'
                    : 'Please draw your signature above to enable signing'}
                </p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSignModal(false)}
                  className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={signProposal}
                  disabled={signing || !hasSignature}
                  className={`flex-1 px-4 py-2 ${
                    hasSignature 
                      ? `${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold` 
                      : `${isDark ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'} cursor-not-allowed`
                  } rounded-sm transition-colors disabled:opacity-50`}
                >
                  {signing ? 'Signing...' : 'Sign Proposal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/90' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-lg`}>
            <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Decline Proposal
              </h2>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mt-1`}>
                We&apos;d appreciate your feedback to help us improve.
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Full Name *</label>
                  <input
                    type="text"
                    value={declineData.decliner_name}
                    onChange={(e) => setDeclineData({ ...declineData, decliner_name: e.target.value })}
                    required
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-red-400 outline-none`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Email *</label>
                  <input
                    type="email"
                    value={declineData.decliner_email}
                    onChange={(e) => setDeclineData({ ...declineData, decliner_email: e.target.value })}
                    required
                    className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-red-400 outline-none`}
                  />
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Reason for declining *</label>
                <select
                  value={declineData.reason}
                  onChange={(e) => setDeclineData({ ...declineData, reason: e.target.value })}
                  required
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-red-400 outline-none`}
                >
                  <option value="">Select a reason...</option>
                  <option value="budget">Budget constraints</option>
                  <option value="timing">Not the right time</option>
                  <option value="competitor">Going with a competitor</option>
                  <option value="not_needed">No longer needed</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Additional feedback (optional)</label>
                <textarea
                  value={declineData.feedback}
                  onChange={(e) => setDeclineData({ ...declineData, feedback: e.target.value })}
                  placeholder="Any additional comments or suggestions..."
                  rows={3}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-red-400 outline-none`}
                />
              </div>
            </div>
            
            <div className={`p-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex gap-3`}>
              <button
                onClick={() => setShowDeclineModal(false)}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={declineProposal}
                disabled={declining || !declineData.reason}
                className={`flex-1 px-4 py-2 ${isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-100 text-red-600 hover:bg-red-200'} rounded-sm transition-colors disabled:opacity-50`}
              >
                {declining ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Styling to match editor */}
      <style>{`
        /* PROPOSAL CONTENT SPACING */
        .proposal-content {
          white-space: normal;
          word-spacing: normal;
          letter-spacing: normal;
        }
        .proposal-content p {
          margin-bottom: 1em;
          line-height: 1.7;
        }
        .proposal-content li {
          line-height: 1.6;
        }
        
        /* Basic typography */
        .prose p {
          margin-top: 1rem;
          margin-bottom: 1rem;
          color: ${isDark ? '#d1d5db' : '#374151'};
        }
        .prose p:empty {
          min-height: 1.5rem;
        }
        .prose br {
          display: block;
          content: "";
          margin-top: 0.5rem;
        }
        .prose h1, .prose h2, .prose h3 {
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: ${isDark ? '#ffffff' : '#111827'};
        }
        .prose ul, .prose ol {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          padding-left: 1.5rem;
          color: ${isDark ? '#d1d5db' : '#374151'};
        }
        .prose ul {
          list-style-type: disc;
        }
        .prose ol {
          list-style-type: decimal;
        }
        .prose li {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
          color: ${isDark ? '#d1d5db' : '#374151'};
          display: list-item;
        }
        .prose blockquote {
          border-left: 3px solid ${isDark ? '#A2FF00' : '#16a34a'};
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: ${isDark ? '#9ca3af' : '#4b5563'};
        }
        .prose strong {
          color: ${isDark ? '#A2FF00' : '#16a34a'};
        }
        .prose a {
          color: ${isDark ? '#A2FF00' : '#16a34a'};
        }
        
        /* TEXT ALIGNMENT - Preserve TipTap alignment styles */
        .prose [style*="text-align: center"],
        .prose [style*="text-align:center"] {
          text-align: center !important;
        }
        .prose [style*="text-align: right"],
        .prose [style*="text-align:right"] {
          text-align: right !important;
        }
        .prose [style*="text-align: justify"],
        .prose [style*="text-align:justify"] {
          text-align: justify !important;
        }
        .prose [style*="text-align: left"],
        .prose [style*="text-align:left"] {
          text-align: left !important;
        }
        
        /* COLUMN LAYOUTS - Preserve grid layouts */
        .prose .column-layout,
        .prose [class*="column-layout"],
        .prose [style*="grid-template-columns"] {
          display: grid !important;
          gap: 1rem;
        }
        .prose .column {
          padding: 1rem;
          min-height: 100px;
        }
        
        /* VIDEO EMBEDS - Preserve responsive video containers */
        .prose .video-embed,
        .prose [class*="video-embed"] {
          position: relative !important;
          padding-bottom: 56.25% !important;
          height: 0 !important;
          overflow: hidden !important;
          margin: 1rem 0 !important;
        }
        .prose .video-embed iframe {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
        }
        
        /* HTML TEMPLATE BLOCKS */
        .prose .html-template-block {
          margin: 1rem 0;
          padding: 1rem;
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
          border-radius: 8px;
          background: ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'};
        }
        
        /* TABLES - Preserve table styling */
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .prose th, .prose td {
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
          padding: 0.75rem;
          text-align: left;
        }
        .prose th {
          background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};
          font-weight: bold;
        }
        
        /* IMAGES - Preserve image styling */
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        
        /* FONT SIZES - Preserve custom font sizes */
        .prose [style*="font-size"] {
          font-size: inherit !important;
        }
        .prose span[style*="font-size: 0.875rem"] { font-size: 0.875rem !important; }
        .prose span[style*="font-size: 1rem"] { font-size: 1rem !important; }
        .prose span[style*="font-size: 1.125rem"] { font-size: 1.125rem !important; }
        .prose span[style*="font-size: 1.25rem"] { font-size: 1.25rem !important; }
        .prose span[style*="font-size: 1.5rem"] { font-size: 1.5rem !important; }
        .prose span[style*="font-size: 2rem"] { font-size: 2rem !important; }
        
        /* COLORS - Preserve text colors */
        .prose [style*="color:"] {
          color: inherit !important;
        }
        
        /* HORIZONTAL RULE */
        .prose hr {
          border: none;
          border-top: 1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default PublicProposal;
